<template>
  <section class="impact-card" aria-labelledby="volunteer-impact-title">
    <div class="impact-hero">
      <div class="impact-hero-copy">
        <div class="impact-eyebrow">
          <span class="impact-spark"><i class="material-icons">volunteer_activism</i></span>
          {{ isAdminView ? 'Volunteer report' : 'Your service story' }}
        </div>
        <h4 id="volunteer-impact-title" class="impact-title">
          {{ isAdminView ? `${displayName}'s Volunteer Impact` : 'My Volunteer Impact' }}
        </h4>
        <p class="impact-subtitle">
          See the days, people, teams, and places touched by your service.
        </p>
      </div>
      <div class="impact-hero-mark" aria-hidden="true">
        <i class="material-icons">auto_awesome</i>
      </div>
    </div>

    <div class="impact-controls">
      <div class="impact-date-fields">
        <label class="impact-date-field">
          <span>From</span>
          <input v-model="fromDate" type="date" :max="toDate" aria-label="Impact report start date">
        </label>
        <span class="impact-date-arrow" aria-hidden="true">→</span>
        <label class="impact-date-field">
          <span>Through</span>
          <input v-model="toDate" type="date" :min="fromDate" aria-label="Impact report end date">
        </label>
        <button class="btn btn-sm btn-primary impact-update-button" type="button" :disabled="loading" @click="loadReport">
          <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="material-icons text-sm align-middle me-1">tune</i>
          Update range
        </button>
      </div>
      <button class="btn btn-sm btn-outline-primary impact-download-button" type="button"
        :disabled="loading || downloading || !report" @click="downloadReport">
        <span v-if="downloading" class="spinner-border spinner-border-sm me-1" role="status"></span>
        <i v-else class="material-icons text-sm align-middle me-1">download</i>
        {{ downloading ? 'Preparing report…' : 'Download official report' }}
      </button>
    </div>

    <div v-if="error" class="impact-message impact-error" role="alert">
      <i class="material-icons">error_outline</i>
      <div>
        <strong>We couldn't load this impact report.</strong>
        <p>{{ error }}</p>
      </div>
      <button class="btn btn-sm btn-outline-danger ms-auto" type="button" @click="loadReport">Retry</button>
    </div>

    <div v-else-if="loading && !report" class="impact-loading">
      <span class="spinner-border text-primary" role="status"></span>
      <span>Gathering your service story…</span>
    </div>

    <template v-else-if="report">
      <div class="impact-range-label">
        <i class="material-icons">date_range</i>
        {{ report.date_range?.label }}
        <span class="impact-range-note">Accepted and pending assignments</span>
      </div>

      <div class="impact-stat-grid">
        <div class="impact-stat impact-stat-days">
          <div class="impact-stat-icon"><i class="material-icons">event_available</i></div>
          <div>
            <strong>{{ formatNumber(report.summary?.volunteer_days) }}</strong>
            <span>Volunteer days</span>
          </div>
        </div>
        <div class="impact-stat impact-stat-assignments">
          <div class="impact-stat-icon"><i class="material-icons">assignment_turned_in</i></div>
          <div>
            <strong>{{ formatNumber(report.summary?.total_assignments) }}</strong>
            <span>Total assignments</span>
            <small class="impact-stat-detail">
              {{ formatNumber(report.summary?.accepted_assignments) }} accepted · {{ formatNumber(report.summary?.pending_assignments) }} pending
            </small>
          </div>
        </div>
        <div class="impact-stat impact-stat-locations">
          <div class="impact-stat-icon"><i class="material-icons">public</i></div>
          <div>
            <strong>{{ formatNumber(report.summary?.location_count) }}</strong>
            <span>Locations served</span>
          </div>
        </div>
        <div class="impact-stat impact-stat-swaps">
          <div class="impact-stat-icon"><i class="material-icons">swap_horiz</i></div>
          <div>
            <strong>{{ formatNumber(report.summary?.swap_count) }}</strong>
            <span>Volunteer swaps</span>
          </div>
        </div>
      </div>

      <div class="impact-encouragement" :class="`impact-tone-${report.encouragement?.tone || 'thanks'}`">
        <div class="impact-encouragement-icon">
          <i class="material-icons">{{ encouragementIcon }}</i>
        </div>
        <div>
          <strong>{{ report.encouragement?.headline }}</strong>
          <p>{{ report.encouragement?.message }}</p>
        </div>
      </div>

      <div class="impact-content-grid">
        <div class="impact-panel impact-month-panel">
          <div class="impact-panel-heading">
            <div>
              <h5>Months you showed up</h5>
              <p>{{ scheduledMonthCount }} of {{ report.monthly_breakdown?.length || 0 }} months include service</p>
            </div>
            <span class="impact-panel-badge"><i class="material-icons">calendar_month</i></span>
          </div>
          <div v-if="scheduledMonths.length" class="impact-month-list">
            <div v-for="month in scheduledMonths" :key="month.key" class="impact-month-row">
              <div class="impact-month-label">{{ month.label }}</div>
              <div class="impact-month-bar-wrap">
                <div class="impact-month-bar" :style="{ width: `${monthBarWidth(month)}%` }"></div>
              </div>
              <div class="impact-month-count">
                <strong>{{ month.scheduled_days }}</strong>
                <span>{{ month.scheduled_days === 1 ? 'day' : 'days' }}</span>
              </div>
              <div class="impact-month-assignments">{{ month.assignment_count }} assignments</div>
            </div>
          </div>
          <div v-else class="impact-empty-inline">
            <i class="material-icons">event_note</i>
            <span>No scheduled service in this range yet.</span>
          </div>
        </div>

        <div class="impact-panel impact-organization-panel">
          <div class="impact-panel-heading">
            <div>
              <h5>Where your impact lands</h5>
              <p>Organizations and locations you serve</p>
            </div>
            <span class="impact-panel-badge impact-panel-badge-purple"><i class="material-icons">diversity_3</i></span>
          </div>
          <div v-if="report.organizations?.length" class="impact-org-list">
            <div v-for="organization in report.organizations" :key="organization.organization_id || organization.organization_name"
              class="impact-org-row">
              <div class="impact-org-avatar"><i class="material-icons">corporate_fare</i></div>
              <div class="impact-org-copy">
                <strong>{{ organization.organization_name }}</strong>
                <span>{{ organization.location_count }} {{ organization.location_count === 1 ? 'location' : 'locations' }} · {{ organization.assignment_count }} assignments</span>
                <div class="impact-location-chips">
                  <span v-for="location in organization.locations" :key="location.location_id" class="impact-location-chip">
                    {{ location.location_name }}
                  </span>
                </div>
              </div>
              <div class="impact-org-days"><strong>{{ organization.volunteer_days }}</strong><span>days</span></div>
            </div>
          </div>
          <div v-else class="impact-empty-inline">
            <i class="material-icons">explore_off</i>
            <span>Your service locations will appear here.</span>
          </div>
        </div>
      </div>

      <div class="impact-highlight-grid">
        <div class="impact-highlight-card">
          <div class="impact-highlight-heading"><i class="material-icons text-success">upcoming</i><span>Next up</span></div>
          <div v-if="report.upcoming_assignments?.length" class="impact-highlight-list">
            <div v-for="assignment in report.upcoming_assignments.slice(0, 3)" :key="assignment.assignment_key" class="impact-highlight-row">
              <div>
                <strong>{{ assignment.activity_name }}</strong>
                <span>{{ assignment.workgroup_name }} · {{ assignment.location_name }}</span>
              </div>
              <time>{{ formatShortDate(assignment.date) }}</time>
            </div>
          </div>
          <p v-else class="impact-empty-text">No upcoming assignments in this range.</p>
        </div>
        <div class="impact-highlight-card">
          <div class="impact-highlight-heading"><i class="material-icons text-primary">history</i><span>Recently served</span></div>
          <div v-if="report.recent_assignments?.length" class="impact-highlight-list">
            <div v-for="assignment in report.recent_assignments.slice(0, 3)" :key="assignment.assignment_key" class="impact-highlight-row">
              <div>
                <strong>{{ assignment.activity_name }}</strong>
                <span>{{ assignment.workgroup_name }} · {{ assignment.location_name }}</span>
              </div>
              <time>{{ formatShortDate(assignment.date) }}</time>
            </div>
          </div>
          <p v-else class="impact-empty-text">Your first service moment in this range is waiting to be celebrated.</p>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import api from '@/config/api'

const props = defineProps({
  personId: { type: [Number, String], required: true },
  locationId: { type: [Number, String], default: null },
  personName: { type: String, default: '' },
  isAdminView: { type: Boolean, default: false },
})

const report = ref(null)
const loading = ref(false)
const downloading = ref(false)
const error = ref('')
const fromDate = ref(defaultFromDate())
const toDate = ref(defaultToDate())

const displayName = computed(() => props.personName || report.value?.person?.name || 'Volunteer')
const scheduledMonths = computed(() => (report.value?.monthly_breakdown || []).filter(month => month.assignment_count > 0))
const scheduledMonthCount = computed(() => scheduledMonths.value.length)
const maxScheduledDays = computed(() => Math.max(1, ...scheduledMonths.value.map(month => Number(month.scheduled_days || 0))))
const encouragementIcon = computed(() => ({
  welcome: 'waving_hand',
  celebrate: 'emoji_events',
  teamwork: 'handshake',
  thanks: 'favorite',
}[report.value?.encouragement?.tone] || 'favorite'))

function formatLocalDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function defaultFromDate() {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - 6)
  return formatLocalDate(date)
}

function defaultToDate() {
  const date = new Date()
  date.setMonth(date.getMonth() + 4, 0)
  return formatLocalDate(date)
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString()
}

function formatShortDate(value) {
  if (!value) return '—'
  const date = new Date(`${value}T12:00:00`)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function monthBarWidth(month) {
  return Math.max(8, Math.round((Number(month.scheduled_days || 0) / maxScheduledDays.value) * 100))
}

function buildParams() {
  return {
    personid: props.personId,
    locationid: props.locationId || undefined,
    from_date: fromDate.value,
    to_date: toDate.value,
  }
}

async function loadReport() {
  error.value = ''
  if (!fromDate.value || !toDate.value || fromDate.value > toDate.value) {
    error.value = 'Choose a start date that is on or before the end date.'
    return
  }

  loading.value = true
  try {
    const response = await api.get('/auth/person-volunteer-impact', { params: buildParams() })
    report.value = response.data?.data || response.data || null
  } catch (requestError) {
    console.error('Error loading volunteer impact:', requestError)
    error.value = requestError.response?.data?.message || 'Please try again in a moment.'
  } finally {
    loading.value = false
  }
}

async function downloadReport() {
  if (!report.value || downloading.value) return
  downloading.value = true
  try {
    const response = await api.get('/auth/person-volunteer-impact/report', {
      params: buildParams(),
      responseType: 'blob',
    })
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${displayName.value.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'volunteer'}-impact-report.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (requestError) {
    console.error('Error downloading volunteer impact report:', requestError)
    error.value = 'The report could not be downloaded. Please try again.'
  } finally {
    downloading.value = false
  }
}

watch(() => [props.personId, props.locationId], () => {
  report.value = null
  loadReport()
})

onMounted(loadReport)
</script>

<style scoped>
.impact-card {
  --impact-ink: #24324a;
  --impact-muted: #71809a;
  --impact-border: #e7edf5;
  background: #fff;
  border: 1px solid var(--impact-border);
  border-radius: 22px;
  box-shadow: 0 14px 38px rgba(36, 50, 74, .1);
  overflow: hidden;
}
.impact-hero {
  min-height: 142px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 34px;
  color: #fff;
  background:
    radial-gradient(circle at 88% 12%, rgba(255,255,255,.24), transparent 25%),
    radial-gradient(circle at 66% 125%, rgba(104,211,145,.35), transparent 36%),
    linear-gradient(120deg, #253b80 0%, #4c5fd5 53%, #1c9b87 100%);
}
.impact-hero-copy { position: relative; z-index: 1; }
.impact-eyebrow { display: flex; align-items: center; gap: 8px; font-size: .72rem; text-transform: uppercase; letter-spacing: 1.3px; opacity: .88; font-weight: 700; }
.impact-spark { width: 27px; height: 27px; display: inline-flex; align-items: center; justify-content: center; border-radius: 9px; background: rgba(255,255,255,.18); }
.impact-spark i { font-size: 17px; }
.impact-title { margin: 10px 0 5px; font-size: 1.55rem; font-weight: 800; letter-spacing: -.3px; color: #fff; }
.impact-subtitle { margin: 0; max-width: 600px; font-size: .82rem; color: rgba(255,255,255,.82); }
.impact-hero-mark { width: 92px; height: 92px; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(255,255,255,.3); border-radius: 50%; color: rgba(255,255,255,.88); transform: rotate(-12deg); background: rgba(255,255,255,.08); box-shadow: 0 0 0 11px rgba(255,255,255,.04), 0 0 0 22px rgba(255,255,255,.025); }
.impact-hero-mark i { font-size: 40px; }
.impact-controls { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 18px 28px 4px; }
.impact-date-fields { display: flex; align-items: end; gap: 9px; flex-wrap: wrap; }
.impact-date-field { display: flex; flex-direction: column; gap: 4px; color: var(--impact-muted); font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; }
.impact-date-field input { min-width: 142px; border: 1px solid #dce4ef; border-radius: 9px; padding: 7px 9px; color: var(--impact-ink); font-size: .78rem; font-weight: 500; background: #fbfcfe; }
.impact-date-field input:focus { outline: 0; border-color: #5669d8; box-shadow: 0 0 0 3px rgba(86,105,216,.13); }
.impact-date-arrow { padding-bottom: 8px; color: #9aa7bb; }
.impact-update-button, .impact-download-button { margin: 0; white-space: nowrap; border-radius: 9px; }
.impact-message { display: flex; align-items: center; gap: 12px; margin: 20px 28px 28px; padding: 15px; border-radius: 12px; }
.impact-message > i { font-size: 26px; }
.impact-message strong { font-size: .85rem; }
.impact-message p { margin: 2px 0 0; font-size: .77rem; }
.impact-error { color: #a33a3a; background: #fff4f4; border: 1px solid #f2d0d0; }
.impact-loading { display: flex; align-items: center; justify-content: center; gap: 10px; min-height: 270px; color: var(--impact-muted); font-size: .85rem; }
.impact-range-label { display: flex; align-items: center; gap: 7px; margin: 20px 28px 14px; color: #52627b; font-size: .78rem; font-weight: 700; }
.impact-range-label i { font-size: 18px; color: #5368d7; }
.impact-range-note { margin-left: 4px; padding-left: 10px; border-left: 1px solid #d6dfeb; color: #9aa7bb; font-size: .7rem; font-weight: 500; }
.impact-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; padding: 0 28px; }
.impact-stat { display: flex; align-items: center; gap: 11px; padding: 15px 13px; border-radius: 14px; }
.impact-stat-icon { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; flex: 0 0 34px; border-radius: 10px; color: #fff; }
.impact-stat-icon i { font-size: 19px; }
.impact-stat strong { display: block; color: var(--impact-ink); font-size: 1.32rem; line-height: 1.1; }
.impact-stat span { display: block; margin-top: 3px; color: #70809a; font-size: .68rem; font-weight: 700; }
.impact-stat-detail { display: block; margin-top: 4px; color: #7f8da3; font-size: .58rem; white-space: nowrap; }
.impact-stat-days { background: #edf8f4; }.impact-stat-days .impact-stat-icon { background: #24a67c; }
.impact-stat-assignments { background: #eef3ff; }.impact-stat-assignments .impact-stat-icon { background: #5268d7; }
.impact-stat-locations { background: #f4efff; }.impact-stat-locations .impact-stat-icon { background: #8059c9; }
.impact-stat-swaps { background: #fff6e8; }.impact-stat-swaps .impact-stat-icon { background: #dc951d; }
.impact-encouragement { display: flex; align-items: center; gap: 13px; margin: 18px 28px 0; padding: 16px 18px; border: 1px solid transparent; border-radius: 14px; }
.impact-encouragement-icon { width: 39px; height: 39px; display: flex; align-items: center; justify-content: center; flex: 0 0 39px; border-radius: 12px; background: rgba(255,255,255,.75); }
.impact-encouragement-icon i { font-size: 22px; }
.impact-encouragement strong { display: block; color: var(--impact-ink); font-size: .9rem; }
.impact-encouragement p { margin: 3px 0 0; color: #596b82; font-size: .76rem; line-height: 1.45; }
.impact-tone-welcome { background: #f1f5ff; border-color: #dbe4ff; }.impact-tone-welcome .impact-encouragement-icon { color: #5268d7; }
.impact-tone-celebrate { background: #fff8e7; border-color: #f5e2ad; }.impact-tone-celebrate .impact-encouragement-icon { color: #d38b16; }
.impact-tone-teamwork { background: #eefbf5; border-color: #cdeedd; }.impact-tone-teamwork .impact-encouragement-icon { color: #22956e; }
.impact-tone-thanks { background: #f3f0ff; border-color: #dfd8fb; }.impact-tone-thanks .impact-encouragement-icon { color: #7652c2; }
.impact-content-grid { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(0, .92fr); gap: 16px; padding: 18px 28px 0; }
.impact-panel { min-width: 0; padding: 18px; border: 1px solid var(--impact-border); border-radius: 15px; background: #fff; }
.impact-panel-heading { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 15px; }
.impact-panel-heading h5 { margin: 0; color: var(--impact-ink); font-size: .92rem; font-weight: 800; }
.impact-panel-heading p { margin: 4px 0 0; color: #8a98ad; font-size: .7rem; }
.impact-panel-badge { width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; border-radius: 10px; color: #22956e; background: #e9f8f2; }
.impact-panel-badge-purple { color: #7652c2; background: #f0ebff; }
.impact-panel-badge i { font-size: 19px; }
.impact-month-list, .impact-org-list { display: flex; flex-direction: column; gap: 11px; }
.impact-month-row { display: grid; grid-template-columns: 85px minmax(70px, 1fr) 48px 83px; align-items: center; gap: 9px; }
.impact-month-label { color: #53627a; font-size: .72rem; font-weight: 700; }
.impact-month-bar-wrap { height: 8px; overflow: hidden; border-radius: 8px; background: #edf1f7; }
.impact-month-bar { height: 100%; min-width: 5px; border-radius: inherit; background: linear-gradient(90deg, #5b72df, #27ab8e); }
.impact-month-count { color: #24324a; text-align: right; line-height: 1; }
.impact-month-count strong { font-size: .85rem; }.impact-month-count span { margin-left: 3px; color: #8a98ad; font-size: .62rem; }
.impact-month-assignments { color: #99a6b8; text-align: right; font-size: .63rem; }
.impact-empty-inline { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 105px; color: #9aa7bb; font-size: .75rem; }
.impact-empty-inline i { font-size: 20px; }
.impact-org-row { display: flex; align-items: flex-start; gap: 10px; }
.impact-org-avatar { width: 31px; height: 31px; display: flex; align-items: center; justify-content: center; flex: 0 0 31px; border-radius: 9px; color: #7652c2; background: #f0ebff; }
.impact-org-avatar i { font-size: 17px; }
.impact-org-copy { min-width: 0; flex: 1; }.impact-org-copy strong { display: block; overflow: hidden; color: #394963; font-size: .76rem; text-overflow: ellipsis; white-space: nowrap; }.impact-org-copy > span { display: block; margin-top: 2px; color: #8a98ad; font-size: .66rem; }
.impact-location-chips { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 5px; }.impact-location-chip { max-width: 150px; overflow: hidden; padding: 2px 7px; border-radius: 10px; color: #6d58a3; background: #f7f3ff; font-size: .6rem; text-overflow: ellipsis; white-space: nowrap; }
.impact-org-days { min-width: 32px; color: #7652c2; text-align: right; line-height: 1; }.impact-org-days strong { display: block; font-size: .9rem; }.impact-org-days span { color: #9aa7bb; font-size: .6rem; }
.impact-highlight-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding: 16px 28px 28px; }
.impact-highlight-card { padding: 15px 17px; border-radius: 14px; background: #f8fafc; }
.impact-highlight-heading { display: flex; align-items: center; gap: 7px; margin-bottom: 10px; color: #40516b; font-size: .77rem; font-weight: 800; text-transform: uppercase; letter-spacing: .5px; }.impact-highlight-heading i { font-size: 18px; }
.impact-highlight-list { display: flex; flex-direction: column; gap: 8px; }.impact-highlight-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding-bottom: 8px; border-bottom: 1px solid #e7edf5; }.impact-highlight-row:last-child { padding-bottom: 0; border-bottom: 0; }.impact-highlight-row strong, .impact-highlight-row span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.impact-highlight-row strong { color: #43536c; font-size: .75rem; }.impact-highlight-row span { max-width: 250px; margin-top: 2px; color: #8b99ad; font-size: .64rem; }.impact-highlight-row time { flex: 0 0 auto; color: #5368d7; font-size: .66rem; font-weight: 700; }
.impact-empty-text { margin: 0; color: #94a1b3; font-size: .72rem; }
@media (max-width: 900px) { .impact-stat-grid { grid-template-columns: repeat(2, 1fr); }.impact-content-grid { grid-template-columns: 1fr; } }
@media (max-width: 620px) { .impact-hero { padding: 22px 20px; }.impact-hero-mark { display: none; }.impact-controls, .impact-range-label, .impact-stat-grid, .impact-content-grid, .impact-highlight-grid { padding-left: 18px; padding-right: 18px; }.impact-range-label { margin-left: 18px; margin-right: 18px; }.impact-controls { align-items: stretch; }.impact-date-fields { width: 100%; }.impact-date-field { flex: 1; }.impact-date-field input { min-width: 0; width: 100%; }.impact-update-button, .impact-download-button { width: 100%; }.impact-month-row { grid-template-columns: 76px minmax(45px, 1fr) 42px; }.impact-month-assignments { display: none; }.impact-highlight-grid { grid-template-columns: 1fr; }.impact-stat-grid { gap: 8px; }.impact-stat { padding: 12px 9px; gap: 7px; }.impact-stat strong { font-size: 1.1rem; }.impact-stat span { font-size: .6rem; }.impact-subtitle { max-width: 275px; } }
</style>
